import React, { useEffect, useState, useRef } from "react";
import backendVerifiedUser from "../../../api/backendVerifiedUser";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

export default function Themes() {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [previewCss, setPreviewCss] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const toast = useRef(null);

  const fetchThemes = async () => {
    setLoading(true);
    try {
      const resp = await backendVerifiedUser.get("/admin/themes");
      setThemes(resp.data || []);
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Could not load themes" });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchThemes();
  }, []);


  const activate = async (id) => {
    try {
      await backendVerifiedUser.put(`/admin/themes/${id}/activate`);
      toast.current?.show({ severity: "success", summary: "Activated", detail: "Theme activated" });
      fetchThemes();
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Activation failed" });
    }
  };

  const uploadAsset = async (themeId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("asset", file);
    try {
      await backendVerifiedUser.post(`/admin/themes/${themeId}/upload-assets`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.current?.show({ severity: "success", summary: "Uploaded", detail: "Asset uploaded" });
      fetchThemes();
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Error", detail: "Upload failed" });
    }
  };

  const previewTheme = async (theme) => {
    // Build a simple mockup HTML and fetch CSS asset content to show preview
    setPreviewCss("");
    setPreviewHtml("");
    if (!theme) return;
    const mockHtml = `
      <div class="preview-root">
        <header class="preview-header p-4">
          <h1>Site Header</h1>
        </header>
        <main class="preview-main p-6">
          <h2>Hero / Banner</h2>
          <p>Sample content to preview theme layout and styles.</p>
          <div class="preview-cards grid grid-cols-3 gap-4 mt-4">
            <div class="card p-4">Card 1</div>
            <div class="card p-4">Card 2</div>
            <div class="card p-4">Card 3</div>
          </div>
        </main>
        <footer class="preview-footer p-4">Site Footer</footer>
      </div>
    `;
    setPreviewHtml(mockHtml);

    if (theme.asset_path) {
      try {
        const resp = await fetch(theme.asset_path);
        if (!resp.ok) throw new Error('Could not fetch asset');
        const cssText = await resp.text();
        setPreviewCss(cssText);
      } catch (e) {
        console.error(e);
        toast.current?.show({ severity: "warn", summary: "Preview", detail: "Could not load asset for preview" });
      }
    } else {
      toast.current?.show({ severity: "info", summary: "Preview", detail: "No asset available for this theme" });
    }
    // scroll into view
    document.getElementById('theme-preview')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="mx-4">
      <Toast ref={toast} />

      

      <div className="card p-4">
        <h3 className="text-lg font-bold mb-3">Available Themes</h3>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <div className="grid grid-cols-1 gap-2">
            {themes.length === 0 && <div>No themes found</div>}
            {themes.map((t) => {
              const files = t.files || [];
              const thumbnailFile = files.find(f => /\.(png|jpe?g|webp|gif|svg)$/i.test(f.name));
              const thumbnail = thumbnailFile ? thumbnailFile.url : null;
              return (
                <div key={t.id} className="border rounded overflow-hidden bg-white shadow-sm">
                  <div className="p-0">
                    <div className="w-full h-40 bg-gray-100 overflow-hidden flex items-center justify-center">
                      {thumbnail ? (
                        <img src={thumbnail} alt={t.name || `Theme ${t.id}`} className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-gray-400">No image</div>
                      )}
                    </div>
                    <div className="p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-lg font-semibold">{t.name || `Theme ${t.id}`}</div>
                          <div className="text-sm text-gray-600 mt-1">{t.meta?.description || ''}</div>
                        </div>
                        <div className="text-right">
                          <div className="mb-2">
                            {t.isActive === 1 ? (
                              <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded">Active</span>
                            ) : (
                              <Button onClick={() => activate(t.id)} className="p-button-sm" label="Activate" />
                            )}
                          </div>
                          <div className="text-xs text-gray-500">ID: {t.id}</div>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <div className="font-medium">Folder</div>
                          <div className="text-gray-600 break-words">{t.folder_name || '-'}</div>
                        </div>
                        <div>
                          <div className="font-medium">Asset</div>
                          <div className="text-gray-600 break-words">{t.asset_path ? <a href={t.asset_path} target="_blank" rel="noreferrer">Open</a> : '-'}</div>
                        </div>
                        <div>
                          <div className="font-medium">Created</div>
                          <div className="text-gray-600">{t.created_at ? new Date(t.created_at).toLocaleString() : '-'}</div>
                        </div>
                        <div>
                          <div className="font-medium">Updated</div>
                          <div className="text-gray-600">{t.updated_at ? new Date(t.updated_at).toLocaleString() : '-'}</div>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center gap-2">
                        <Button label="Preview" onClick={() => previewTheme(t)} />
                        <label>
                          <input
                            type="file"
                            accept="image/*, text/css, .css, .zip"
                            onChange={(e) => uploadAsset(t.id, e.target.files[0])}
                            className="hidden"
                            id={`asset-${t.id}`}
                          />
                          <Button label="Upload Asset" onClick={() => document.getElementById(`asset-${t.id}`).click()} />
                        </label>
                      </div>
                      {files.length > 0 && (
                        <div className="mt-3">
                          <div className="font-medium text-sm">Files</div>
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {files.map(f => (
                              <a key={f.url} href={f.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">{f.name}</a>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mt-3">
                        <Button
                          className="p-button-text p-button-plain"
                          label={expanded[t.id] ? 'Hide raw data' : 'Show raw data'}
                          onClick={() => setExpanded(prev => ({ ...prev, [t.id]: !prev[t.id] }))}
                        />
                        {expanded[t.id] && (
                          <pre className="mt-2 p-2 bg-gray-100 text-xs overflow-auto" style={{ maxHeight: 240 }}>{JSON.stringify(t, null, 2)}</pre>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div id="theme-preview" className="card p-4 mt-4">
        <h3 className="text-lg font-bold mb-3">Preview</h3>
        <div className="border p-3">
          {previewCss && <style>{previewCss}</style>}
          <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </div>
      </div>
    </div>
  );
}
